import classnames from 'classnames';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCloseDropdownOnClickOutside } from '@/shared/hooks/useCloseDropdownOnClickOutside';
import { DotsIcon } from '@/shared/assets/DotsIcon';

import { DropList } from './_DropList';
import styles from './_TabList.module.css';
import { TabItem } from './_TabItem';

interface Tab {
    title: string;
    id: string;
}

interface TabListProps {
    tabs: Tab[];
    onChange: (id: Tab['id']) => void;
    selected?: Tab['id'];
    className?: string;
    itemClassName?: string;
    hasDropDownList?: boolean;
    dropDownButtonLabel?: string;
    autoFocus?: boolean;
}

type TabsWidthMap = Record<Tab['id'], number | undefined>;

interface SplitTabsResult {
    mainList: Tab[];
    dropList: Tab[];
    isCorrected: boolean;
}

const getSplitedTabs = (
    tabWidthMap: TabsWidthMap,
    availableWidth: number,
    tabs: Tab[]
): SplitTabsResult => {
    let sumWidth = 0;
    const mainList: Tab[] = [];
    const dropList: Tab[] = [];

    tabs.forEach((tab) => {
        const tabWidth = tabWidthMap[tab.id] || 0;

        if (sumWidth + tabWidth < availableWidth) {
            mainList.push(tab);
            sumWidth += tabWidth;
        } else {
            dropList.push(tab);
        }
    });

    // Если в dropdown только один элемент, перемещаем последний из main в dropdown
    if (dropList.length === 1 && mainList.length > 0) {
        const lastMainTab = mainList[mainList.length - 1];

        if (lastMainTab) {
            return {
                mainList: mainList.slice(0, -1),
                dropList: [lastMainTab, ...dropList],
                isCorrected: true,
            };
        }
    }

    return {
        mainList,
        dropList,
        isCorrected: false,
    };
};

export const TabList: FunctionComponent<TabListProps> = (props) => {
    const {
        tabs,
        selected,
        onChange,
        className,
        itemClassName,
        hasDropDownList = false,
        dropDownButtonLabel,
        autoFocus,
    } = props;

    const dropDownRef = useRef<HTMLUListElement | null>(null);
    const tabListRef = useRef<HTMLUListElement | null>(null);
    const dotsRef = useRef<HTMLLIElement | null>(null);

    const [tabWidthMap, setTabWidthMap] = useState<TabsWidthMap>({});
    const [dimensions, setDimensions] = useState({
        tabListWidth: 0,
        dotsWidth: 0,
    });
    const [isShowDropList, setIsShowDroplist] = useState(false);

    const handleTabClick = useCallback(
        (id: string) => {
            onChange(id);
            setIsShowDroplist(false);
        },
        [onChange]
    );

    const handleDotsClick = useCallback(() => {
        setIsShowDroplist((prev) => !prev);
    }, []);

    const handleWidthChange = useCallback((width: number, id: Tab['id']) => {
        setTabWidthMap((prev) => ({
            ...prev,
            [id]: width,
        }));
    }, []);

    const updateDimensions = useCallback(() => {
        const tabListWidth = tabListRef.current?.offsetWidth || 0;
        const dotsWidth = dotsRef.current?.offsetWidth || 0;

        setDimensions({ tabListWidth, dotsWidth });
    }, []);

    const splitTabs = useMemo<SplitTabsResult>(() => {
        if (!hasDropDownList || tabs.length < 2) {
            return {
                mainList: tabs,
                dropList: [],
                isCorrected: false,
            };
        }

        const availableWidth = dimensions.tabListWidth - dimensions.dotsWidth;

        return getSplitedTabs(tabWidthMap, availableWidth, tabs);
    }, [hasDropDownList, tabs, dimensions.tabListWidth, dimensions.dotsWidth, tabWidthMap]);

    const isNeedDots = hasDropDownList && splitTabs.dropList.length > 0;

    useEffect(() => {
        if (!hasDropDownList) {
            return;
        }

        updateDimensions();
        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [hasDropDownList, isNeedDots, updateDimensions]);

    useCloseDropdownOnClickOutside(dropDownRef, dotsRef, setIsShowDroplist);

    const visibleTabs = hasDropDownList ? splitTabs.mainList : tabs;

    return (
        // https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ul className={classnames(styles.root, className)} ref={tabListRef} role="list">
            {visibleTabs.map(({ title, id }, index) => (
                <TabItem
                    key={id}
                    id={id}
                    selected={id === selected}
                    onClick={() => handleTabClick(id)}
                    className={itemClassName}
                    setWidth={handleWidthChange}
                    autoFocus={index === 0 && autoFocus}
                >
                    {title}
                </TabItem>
            ))}
            {isNeedDots && (
                <li className={styles.dropdown} ref={dotsRef}>
                    <button
                        className={styles.dropdownButton}
                        onClick={handleDotsClick}
                        aria-expanded={isShowDropList}
                        aria-label={dropDownButtonLabel}
                    >
                        <DotsIcon className={styles.dots} aria-hidden />
                    </button>
                    {isShowDropList && (
                        <DropList
                            innerRef={dropDownRef}
                            items={splitTabs.dropList}
                            onItemClick={handleTabClick}
                            selectedId={selected}
                            className={classnames(styles.dropList, {
                                [styles.dropListCorrected]: splitTabs.isCorrected,
                            })}
                        />
                    )}
                </li>
            )}
        </ul>
    );
};
